import React, {useState, useEffect} from "react";
import {useDispatch} from "react-redux";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Box,
    Typography,
    Chip,
    Avatar,
    Fade,
    Zoom,
    Card,
    CardContent,
    CardMedia,
    Grid,
    Divider,
    Stack,
} from "@mui/material";
import {styled} from "@mui/material/styles";
import 'react-toastify/dist/ReactToastify.css';
import {toast, ToastContainer} from 'react-toastify';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ImageIcon from "@mui/icons-material/Image";
import InventoryIcon from "@mui/icons-material/Inventory";
import {deleteSchoolAsset} from "./redux/SchoolAssetAction";

// Styled Components
const StyledTableContainer = styled(TableContainer)(({theme}) => ({
    maxHeight: 520,
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    '&::-webkit-scrollbar': {
        width: '8px',
    },
    '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
        background: '#888',
        borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        background: '#555',
    },
}));

const StyledTableHead = styled(TableHead)(({theme}) => ({
    backgroundColor: theme.palette.primary.main,
    '& .MuiTableCell-head': {
        color: theme.palette.common.white,
        fontWeight: 700,
        fontSize: '0.95rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
}));

const StyledTableRow = styled(TableRow)(({theme}) => ({
    transition: 'all 0.3s ease',
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
        transform: 'scale(1.01)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    '& > td': {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
}));

const ActionButton = styled(IconButton)(({theme, color}) => ({
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'scale(1.2) rotate(5deg)',
        backgroundColor: color === 'error' ? 'rgba(211, 47, 47, 0.1)' :
                         color === 'secondary' ? 'rgba(156, 39, 176, 0.1)' :
                         'rgba(25, 118, 210, 0.1)',
    },
}));

const ModernDialog = styled(Dialog)(({theme}) => ({
    '& .MuiDialog-paper': {
        borderRadius: 16,
        padding: theme.spacing(2),
    },
}));

const convertByteArrayToBase64 = (byteArray) => {
    if (!byteArray) return '';
    if (typeof byteArray === 'string' && byteArray.startsWith('data:image')) {
        return byteArray;
    }
    return `data:image/jpeg;base64,${byteArray}`;
};
const SchoolAssetList = ({schoolAssets, onEdit, onView}) => {
    const dispatch = useDispatch();
    const [modalOpen, setModalOpen] = useState(false);
    const [assetToDelete, setAssetToDelete] = useState(null);
    const [assets, setAssets] = useState(schoolAssets);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);

    useEffect(() => {
        setAssets(schoolAssets);
    }, [schoolAssets]);


    const handleOpenModal = (id) => {
        setAssetToDelete(id);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setAssetToDelete(null);
    };

    const handleViewAsset = (asset) => {
        setSelectedAsset(asset);
        setViewDialogOpen(true);
    };

    const handleCloseViewDialog = () => {
        setViewDialogOpen(false);
        setSelectedAsset(null);
    };

    const handleToastDelete = () => {
        if (assetToDelete) {
            dispatch(deleteSchoolAsset(assetToDelete))
                .then(() => {
                    toast.success("School asset deleted successfully.", {
                        position: "top-right",
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                    setAssets(prevAssets => prevAssets.filter(asset => asset.id !== assetToDelete));
                    handleCloseModal();
                })
                .catch((error) => {
                    console.error("Error deleting asset:", error);
                    toast.error("Failed to delete the asset. Please try again.", {
                        position: "top-right",
                        autoClose: 3000,
                    });
                    handleCloseModal();
                });
        }
    };

    return (
        <>
            <StyledTableContainer component={Paper}>
                <Table stickyHeader>
                    <StyledTableHead>
                        <TableRow>
                            <TableCell>Asset Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="center">Quantity</TableCell>
                            <TableCell align="center">Photo</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </StyledTableHead>
                    <TableBody>
                        {assets && assets.length > 0 ? (
                            assets.map((asset) => (
                                <StyledTableRow key={asset.id}>
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <InventoryIcon color="primary" />
                                            <Typography variant="body1" fontWeight={500}>
                                                {asset.assetName}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={asset.type}
                                            color="primary"
                                            size="small"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={asset.quantity}
                                            color="secondary"
                                            variant="outlined"
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        {asset.assetPhoto ? (
                                            <Zoom in={true}>
                                                <Avatar
                                                    src={convertByteArrayToBase64(asset.assetPhoto)}
                                                    alt={asset.assetName}
                                                    sx={{
                                                        width: 50,
                                                        height: 50,
                                                        margin: 'auto',
                                                        border: '2px solid',
                                                        borderColor: 'primary.main',
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.3s ease',
                                                        '&:hover': {
                                                            transform: 'scale(1.2)',
                                                        }
                                                    }}
                                                    onClick={() => handleViewAsset(asset)}
                                                />
                                            </Zoom>
                                        ) : (
                                            <Avatar sx={{ margin: 'auto', bgcolor: 'grey.300' }}>
                                                <ImageIcon />
                                            </Avatar>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="View Details" arrow TransitionComponent={Fade}>
                                            <ActionButton
                                                onClick={() => handleViewAsset(asset)}
                                                color="primary"
                                                size="small"
                                            >
                                                <VisibilityIcon />
                                            </ActionButton>
                                        </Tooltip>
                                        <Tooltip title="Edit Asset" arrow TransitionComponent={Fade}>
                                            <ActionButton
                                                onClick={() => onEdit(asset)}
                                                color="secondary"
                                                size="small"
                                            >
                                                <EditIcon />
                                            </ActionButton>
                                        </Tooltip>
                                        <Tooltip title="Delete Asset" arrow TransitionComponent={Fade}>
                                            <ActionButton
                                                onClick={() => handleOpenModal(asset.id)}
                                                color="error"
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </ActionButton>
                                        </Tooltip>
                                    </TableCell>
                                </StyledTableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Box py={4}>
                                        <InventoryIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                                        <Typography variant="h6" color="text.secondary">
                                            No assets found
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </StyledTableContainer>

            {/* Delete Confirmation Dialog */}
            <ModernDialog
                open={modalOpen}
                onClose={handleCloseModal}
                TransitionComponent={Zoom}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    color: 'error.main',
                    fontWeight: 700,
                }}>
                    <WarningAmberIcon sx={{ fontSize: 32 }} />
                    Delete School Asset?
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: 'grey.500',
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ py: 3 }}>
                    <DialogContentText sx={{ fontSize: '1rem' }}>
                        Are you sure you want to delete this school asset? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseModal}
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleToastDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        sx={{ borderRadius: 2 }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </ModernDialog>

            {/* View Asset Dialog */}
            <ModernDialog
                open={viewDialogOpen}
                onClose={handleCloseViewDialog}
                TransitionComponent={Fade}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                }}>
                    <InventoryIcon />
                    Asset Details
                    <IconButton
                        onClick={handleCloseViewDialog}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: 'white',
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {selectedAsset && (
                        <Card elevation={0}>
                            {selectedAsset.assetPhoto && (
                                <CardMedia
                                    component="img"
                                    height="300"
                                    image={convertByteArrayToBase64(selectedAsset.assetPhoto)}
                                    alt={selectedAsset.assetName}
                                    sx={{ objectFit: 'contain', bgcolor: 'grey.100' }}
                                />
                            )}
                            <CardContent sx={{ p: 3 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <Typography variant="h4" gutterBottom fontWeight={700}>
                                            {selectedAsset.assetName}
                                        </Typography>
                                        <Chip
                                            label={selectedAsset.type}
                                            color="primary"
                                            sx={{ mb: 2 }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Box sx={{
                                            p: 2,
                                            bgcolor: 'primary.lighter',
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: 'primary.light',
                                        }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Quantity
                                            </Typography>
                                            <Typography variant="h5" fontWeight={700} color="primary">
                                                {selectedAsset.quantity}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {selectedAsset.description && (
                                        <Grid item xs={12}>
                                            <Divider sx={{ my: 1 }} />
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Description
                                            </Typography>
                                            <Typography variant="body1">
                                                {selectedAsset.description}
                                            </Typography>
                                        </Grid>
                                    )}

                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                        <Stack spacing={1}>
                                            {selectedAsset.schoolId && (
                                                <Box display="flex" justifyContent="space-between">
                                                    <Typography variant="body2" color="text.secondary">
                                                        School ID:
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {selectedAsset.schoolId}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {selectedAsset.session && (
                                                <Box display="flex" justifyContent="space-between">
                                                    <Typography variant="body2" color="text.secondary">
                                                        Session:
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {selectedAsset.session}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {selectedAsset.creationDateTime && (
                                                <Box display="flex" justifyContent="space-between">
                                                    <Typography variant="body2" color="text.secondary">
                                                        Created:
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {new Date(selectedAsset.creationDateTime).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
                    <Button
                        onClick={() => {
                            handleCloseViewDialog();
                            onEdit(selectedAsset);
                        }}
                        variant="outlined"
                        startIcon={<EditIcon />}
                        sx={{ borderRadius: 2 }}
                    >
                        Edit Asset
                    </Button>
                    <Button
                        onClick={handleCloseViewDialog}
                        variant="contained"
                        sx={{ borderRadius: 2 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </ModernDialog>

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />
        </>
    );
};

export default SchoolAssetList;


